<template>
  <div class="relative z-0 flex h-full overflow-auto">
    <UpdateTeam v-if="team_to_be_updated" :team="team_to_be_updated" />
    <main v-else class="flex-1 mx-4 mt-3">
      <div v-if="teams?.length > 0" class="inline-block min-w-full align-middle">
        <table class="min-w-full border rounded-lg border-separate overflow-visible shadow-md">
          <thead class="bg-gray-100 dark:bg-gray-800">
            <tr>
              <th class="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">
                {{ $t("Team") }}
              </th>
              <th class="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">
                {{ $t("Team Manager") }}
              </th>
              <th class="text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300">
                {{ $t("Members") }}
              </th>
              <th class="hidden text-left px-2 py-2 font-semibold text-gray-600 dark:text-gray-300 md:table-cell">
                {{ $t("Last updated") }}
              </th>
              <th class="px-2 py-2 font-semibold text-gray-600 dark:text-gray-300 text-right">
                {{ $t("Actions") }}
              </th>
            </tr>
          </thead>

          <tbody class="bg-white dark:bg-gray-800">
            <tr v-for="team in teams" :key="team.id" class="dark:hover:bg-gray-700 transition-colors">
              <td class="px-2 py-2 text-[16px] font-normal">
                <span class="truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">{{ team.name }}</span>
              </td>

              <td class="hidden md:table-cell px-2 py-2 text-[16px] font-normal">
                <span>{{ String(team.team_managers.map((t) => t.first_name + " " + t.last_name)) }}</span>
              </td>

              <td class="px-2 py-2 text-[16px] font-normal">
                <div class="flex -space-x-3">
                  <div v-for="member in team.users" :key="member.id" class="relative inline-block">
                    <img
                      v-if="member?.avatar"
                      :src="member.avatar"
                      onerror="this.onerror=null; this.src=window.default_avatar;"
                      class="w-10 h-10 rounded-full border-2 border-white shadow-md"
                      alt="User avatar">
                    <Avatar v-else :first-name="member.first_name" :last-name="member.last_name" />
                    <span class="tooltip p-2 bg-gray-300 rounded-md dark:bg-gray-700">
                      <p class="text-sm font-normal text-gray-800 dark:text-white">
                        {{ member.full_name }}
                      </p>
                    </span>
                  </div>
                </div>
              </td>

              <td class="hidden md:table-cell px-2 py-2 text-[16px]">
                {{ $moment(team.last_edited).format("MMMM D, YYYY") }}
              </td>

              <td class="table-cell px-2 py-2 text-[16px] text-right">
                <div class="flex w-full justify-end">
                  <ActionDropDown :icon-button="true" :icon="EllipsisVertical" :actions="getTeamActions(team)" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-else
        class="flex justify-center items-center min-h-[300px] rounded-lg bg-gradient-to-r from-gray-100 to-gray-300 dark:from-gray-800 dark:to-gray-900 shadow-inner">
        <div class="flex flex-col items-center">
          <h3 class="mt-4 text-2xl font-semibold text-gray-900 dark:text-white">
            {{ $t("No Teams Available") }}
          </h3>
          <p class="mt-3 text-lg text-gray-600 dark:text-gray-400">
            {{ $t("Create a new team to start collaborating effectively.") }}
          </p>
          <button
            class="flex items-center mt-6 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            @click="createTeam()">
            <Plus class="w-5 h-5 mr-2" /> {{ $t("Create Your First Team") }}
          </button>
        </div>
      </div>

      <label class="block mt-6 mb-2 text-lg font-semibold text-gray-800 dark:text-white">{{ $t("All Users") }}</label>
      <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <li
          v-for="user in $store.state.data.users.sort((a, b) =>
            a.role.name == 'Administrator' && b.role.name != 'Administrator' ? -1 : 1
          )"
          :key="user.id"
          class="relative col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
          <div class="flex w-full items-center justify-between space-x-6 p-6">
            <div class="flex-1 truncate">
              <div class="flex items-center space-x-3">
                <h3 class="truncate text-md font-medium text-gray-900">
                  {{ user.full_name }}
                </h3>
                <span
                  class="inline-flex flex-shrink-0 items-center rounded-full bg-green-50 px-1.5 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">{{ user.role_name }}</span>
              </div>
              <p class="mt-1 truncate text-sm text-gray-500">
                {{ user.job_title }}
              </p>
            </div>
            <Avatar :radius="48" :hover-effect="true" :user="user" />

            <div class="absolute top-1 right-1">
              <ActionDropDown :icon-button="true" :icon="EllipsisVertical" :actions="getUserActions(user)" />
            </div>
          </div>
          <div>
            <div class="-mt-px flex divide-x divide-gray-200">
              <div class="flex w-0 flex-1">
                <a
                  :href="`mailto:${user.email}`"
                  class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                  <Mail class="h-5 w-5 text-gray-400" aria-hidden="true" />
                  {{ $t("Email") }}
                </a>
              </div>
              <div class="-ml-px flex w-0 flex-1">
                <div
                  class="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                  <KeyRound class="h-5 w-5 text-gray-200" color="#0002E1" aria-hidden="true" />
                  <div class="text-sm text-gray-600 dark:text-gray-300 text-right">
                    <p>
                      {{ user.last_login ? `${$moment(user.last_login).format("DD MMM YYYY")}` : $t("Never") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </main>
    <Modal v-model:is-open="show_change_role_modal" :default-success-msg="$t('User role changed successfully')" :use-toast="true">
      <template #form="{ close: closeModal }">
        <div class="flex flex-col items-start justify-start h-full px-4">
          <Listbox v-model="target_user.role" as="div">
            <ListboxLabel> {{ $t("Select a new role for") }} {{ target_user.full_name }} </ListboxLabel>
            <div class="relative">
              <ListboxButton
                class="relative inline-flex items-center rounded-full py-2 px-2 bg-gray-50 dark:bg-dark-box-background text-sm font-medium text-gray-500 whitespace-nowrap hover:bg-gray-100 sm:px-3">
                <Tag
                  :class="[
                    target_user.role === null ? 'text-gray-300' : 'text-gray-500 dark:text-gray-200',
                    'flex-shrink-0 h-5 w-5 sm:-ml-1'
                  ]"
                  aria-hidden="true" />
                <span :class="[target_user.role === null ? '' : 'text-gray-900 dark:text-gray-200', 'hidden truncate sm:ml-2 sm:block']">{{
                  target_user.role === null
                    ? "Label"
                    : $store.state.auth.user.organization.roles.find((x) => x.id == target_user.role.id)?.name
                }}</span>
              </ListboxButton>

              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions
                  class="absolute right-0 z-10 mt-1 w-52 bg-white dark:bg-dark-box-background shadow max-h-56 rounded-lg py-3 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm text-gray-700 hover:cursor-pointer hover:text-gray-700">
                  <ListboxOption
                    v-for="role in $store.state.auth.user.organization.roles"
                    :key="role.name"
                    v-slot="{ active }"
                    :value="role"
                    as="template">
                    <li
                      :class="[
                        active ? 'bg-gray-100 dark:bg-common-grey' : 'bg-white dark:bg-dark-box-background',
                        'cursor-default select-none relative py-2 px-3'
                      ]">
                      <div class="flex items-center">
                        <span class="block font-medium truncate dark:text-white">
                          {{ role.name }}
                        </span>
                      </div>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
          <div class="flex w-full justify-between mt-20">
            <button class="btn-cancel" @click="closeModal">{{ $t("Cancel") }}</button>
            <button class="btn-primary w-fit" @click="changeRole">{{ $t("Save") }}</button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import authService from "@/services/auth.service"
import UpdateTeam from "./UpdateTeam.vue"
import { Plus, Trash2, EllipsisVertical, Mail, Pencil, Tag, KeyRound } from "lucide-vue-next"
import ActionDropDown from "@/components/shared/ActionDropDown.vue"
import Avatar from "@/components/shared/Avatar.vue"
import Modal from "@/components/shared/Modal.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { markRaw } from "vue"
import { ListboxLabel, Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue"

export default {
  components: {
    ListboxLabel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Tag,
    Plus,
    KeyRound,
    Mail,
    Avatar,
    ActionDropDown,
    UpdateTeam,
    Modal
  },
  data() {
    return {
      EllipsisVertical: markRaw(EllipsisVertical),
      target_user: null,
      team_to_be_deleted: null,
      team_to_be_updated: null,
      show_change_role_modal: false,
      teams: []
    }
  },
  created() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Teams"])
    this.retrieveTeams()
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  methods: {
    changeRole() {
      authService
        .changeUserRole(this.target_user.id, { role_id: this.target_user.role.id })
        .then(() => {
          this.$store.dispatch("auth/fetchUsers")
          this.show_change_role_modal = false
        })
        .catch((err) => {
          this.$toast.error(err.response.data.description, { duration: 5000, position: "top" })
        })
    },
    createTeam() {
      this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["New Team"])
      this.$router.push({ name: "NewTeam" })
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.ADD_TEAM, () => {
        this.createTeam()
      })
      this.$emitter.on(ALL_EMITTERS.REFRESH_TEAMS, () => {
        this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Teams"])
        this.team_to_be_updated = null
        this.retrieveTeams()
      })
      this.$emitter.on(ALL_EMITTERS.GO_BACK, () => {
        this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Teams"])
        this.team_to_be_updated = null
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.ADD_TEAM)
      this.$emitter.off(ALL_EMITTERS.REFRESH_TEAMS)
      this.$emitter.off(ALL_EMITTERS.GO_BACK)
    },
    getUserActions(user) {
      let actions = [
        {
          name: this.$t("Delete"),
          action: () => {
            this.target_user = user
            this.deleteUser()
          },
          icon: Trash2
        }
      ]
      let admin_actions = [
        {
          name: this.$t("Change Role"),
          action: () => {
            this.target_user = user
            this.show_change_role_modal = true
          },
          icon: Pencil
        }
      ]
      if (this.$store.state.data.users.find((u) => u.id == this.$store.state.auth.user.id).role_name == "Administrator") {
        actions = [...actions, ...admin_actions]
      }
      return actions
    },
    getTeamActions(team) {
      return [
        {
          name: this.$t("Edit"),
          action: () => {
            this.setTeamToBeUpdated(team)
          },
          icon: Pencil
        },
        {
          name: this.$t("Delete"),
          action: () => {
            this.team_to_be_deleted = team
            this.deleteTeam()
          },
          icon: Trash2
        }
      ]
    },

    setTeamToBeUpdated(team) {
      this.team_to_be_updated = { ...team }
    },
    deleteTeam() {
      this.$emitter.emit(ALL_EMITTERS.GENERIC_DELETE_MODAL, {
        title: this.$t("Delete Team"),
        success_message: this.$t("Team") + ` ${this.team_to_be_deleted.name} ` + this.$t("deleted successfully"),
        message: this.$t("Once deleted, the team cannot be restored. Are you sure you want to proceed?"),
        action: () => {
          dataService.deleteOrganizationTeam(this.team_to_be_deleted.id).then(() => {
            this.teams = this.teams.filter((team) => team.id != this.team_to_be_deleted.id)
            this.team_to_be_deleted = null
          })
        }
      })
    },
    deleteUser() {
      this.$emitter.emit(ALL_EMITTERS.GENERIC_DELETE_MODAL, {
        title: this.$t("Delete User"),
        success_message: this.$t("User") + ` ${this.target_user.full_name} ` + this.$t("deleted successfully"),
        message: this.$t("Once deleted, the user cannot be restored. Are you sure you want to proceed?"),
        action: () => {
          authService.deleteUser(this.target_user.id).then(() => {
            this.$store.dispatch("auth/fetchUsers")
            this.target_user = null
          })
        }
      })
    },

    retrieveTeams() {
      dataService
        .getOrganizationTeams()
        .then((res) => {
          this.teams = res.data.results
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }
}
</script>

<style scoped>
.tooltip {
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: none;
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 0.875rem;
  border-radius: 0.25rem;
}

.relative:hover .tooltip {
  display: block;
}
</style>
